import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/shared/hooks/use-toast";
import { logger } from '@/shared/utils/logger';
import { computeMfaGate } from "@/core/auth/aalFromSession";
import { useMfaRequiredRoles } from "@/core/auth/useMfaRequiredRoles";

// Module-level flag: suppresses SIGNED_OUT redirect while impersonation is switching sessions
let impersonationInProgress = false;
export function setImpersonationInProgress(value: boolean) {
  impersonationInProgress = value;
}

type Profile = {
  id: string;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  is_active: boolean;
};

export type UserRole = {
  id: string;
  role_code: string;
  label: string;
  description: string | null;
};

interface AuthStateContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  isLoading: boolean;
  hasRole: (roleCode: string) => boolean;
  verifyRoleServerSide: (roleCode: string) => Promise<boolean>;
  /** Il ruolo dell'utente richiede MFA (calcolato sincronicamente da session+roles). */
  mfaRequired: boolean;
  /** Requisito MFA soddisfatto: non serve MFA, oppure sessione già aal2. Fail-closed in caso di dubbio. */
  mfaSatisfied: boolean;
}

const AuthStateContext = createContext<AuthStateContextType | undefined>(undefined);

/**
 * @description Context provider that manages authentication state: current user, session, profile, roles, and loading status.
 * Listens to Supabase auth state changes and loads user profile/roles on sign-in.
 * @param {{ children: ReactNode }} props - React children to wrap.
 */
export const AuthStateProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Carica il profilo dell'utente
  const loadUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        logger.error("Errore nel caricamento del profilo:", error);
        return;
      }

      // Block deactivated users
      if (data && data.is_active === false) {
        logger.warn("Account disattivato, disconnessione in corso...");
        toast({
          title: "Account disattivato",
          description: "Il tuo account e stato disattivato. Contatta un amministratore.",
          variant: "destructive",
        });
        await supabase.auth.signOut();
        return;
      }

      setProfile(data as Profile);
    } catch (error) {
      logger.error("Errore nel caricamento del profilo:", error);
    }
  };

  // Carica i ruoli dell'utente
  const loadUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("roles:role_id(id, role_code, label, description)")
        .eq("user_id", userId);

      if (error) {
        logger.error("Errore nel caricamento dei ruoli:", error);
        return;
      }

      // Estrae i ruoli dalla risposta nidificata
      const userRoles = data.map((item: any) => item.roles) as UserRole[];
      setRoles(userRoles);
    } catch (error) {
      logger.error("Errore nel caricamento dei ruoli:", error);
    }
  };

  // Verifica se l'utente ha un determinato ruolo (client-side, veloce ma non sicuro per operazioni sensibili)
  const hasRole = (roleCode: string): boolean => {
    return roles.some(role => role.role_code === roleCode);
  };

  // Verifica server-side se l'utente ha un determinato ruolo (sicuro, per operazioni sensibili)
  const verifyRoleServerSide = useCallback(async (roleCode: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('rpc_verify_user_role', { _role_code: roleCode });

      if (error) {
        // Fallback al controllo client-side se RPC non disponibile
        logger.warn('Server-side role verification failed, using client-side fallback:', error.message);
        return hasRole(roleCode);
      }

      return data === true;
    } catch (err) {
      logger.error('Error verifying role server-side:', err);
      // Fallback al controllo client-side
      return hasRole(roleCode);
    }
  }, [roles]);

  useEffect(() => {
    // Configura il listener per i cambiamenti di autenticazione
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        logger.debug('🔄 Auth state changed:', event, {
          hasSession: !!currentSession,
          userId: currentSession?.user?.id
        });
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (event === 'SIGNED_OUT') {
          // Clear all auth-related state immediately
          setProfile(null);
          setRoles([]);
          setUser(null);
          setSession(null);

          // Skip redirect if impersonation is switching sessions (signOut → setSession)
          if (!impersonationInProgress && !window.location.pathname.startsWith('/auth')) {
            const isDeviceSession = localStorage.getItem('device-session') === 'true';
            window.location.href = isDeviceSession ? '/device' : '/auth';
          }
        } else if (currentSession?.user) {
          // Load user data asynchronously without blocking
          loadUserProfile(currentSession.user.id);
          loadUserRoles(currentSession.user.id);
        } else {
          setProfile(null);
          setRoles([]);
        }
      }
    );

    // Controlla se c'è già una sessione attiva
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        // Attendere ENTRAMBI prima di settare isLoading=false
        // per evitare race condition con ProtectedRoute
        await Promise.all([
          loadUserProfile(currentSession.user.id),
          loadUserRoles(currentSession.user.id)
        ]);
      }

      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Lista dei ruoli che richiedono MFA, dal DB con fallback hardcoded (placeholderData).
  // Disponibile sincronicamente al primo render grazie al placeholder → nessun flash.
  const { data: mfaRequiredRoles } = useMfaRequiredRoles();

  // Gate MFA calcolato SINCRONICAMENTE dal claim `aal` nel JWT + ruoli.
  // Fail-closed: se aal non decodificabile su ruolo che richiede MFA → mfaSatisfied=false.
  const { mfaRequired, mfaSatisfied } = useMemo(
    () => computeMfaGate(session, roles.map(r => r.role_code), mfaRequiredRoles),
    [session, roles, mfaRequiredRoles]
  );

  const value = {
    user,
    session,
    profile,
    roles,
    isLoading,
    hasRole,
    verifyRoleServerSide,
    mfaRequired,
    mfaSatisfied,
  };

  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>;
};

/**
 * @description Hook to access authentication state from AuthStateContext.
 * @returns {{ user: User | null, session: Session | null, profile: Profile | null, roles: UserRole[], isLoading: boolean, hasRole: (roleCode: string) => boolean, verifyRoleServerSide: (roleCode: string) => Promise<boolean> }}
 */
export const useAuthState = () => {
  const context = useContext(AuthStateContext);
  if (context === undefined) {
    // In development, provide more helpful error information
    if (process.env.NODE_ENV === 'development') {
      logger.error(
        '🚨 useAuthState Error: Hook used outside AuthStateProvider\n' +
        'Make sure your component is wrapped with <AuthStateProvider>\n' +
        'Current component tree should include:\n' +
        '  <AuthStateProvider>\n' +
        '    <YourComponent />\n' +
        '  </AuthStateProvider>'
      );
      
      // In development, throw the error to help developers fix the issue
      throw new Error("useAuthState deve essere utilizzato all'interno di un AuthStateProvider");
    } else {
      // In production, provide a fallback to prevent crashes
      logger.warn('useAuthState used outside provider, returning fallback values');
      return {
        user: null,
        session: null,
        profile: null,
        roles: [],
        isLoading: false,
        hasRole: () => false,
        verifyRoleServerSide: async () => false,
        mfaRequired: false,
        mfaSatisfied: true,
      };
    }
  }
  return context;
};
